import { StringDevUIFieldDescriptor } from "./StringDevUISchema";
import { StringDevUIField } from "./fields/StringDevUIField";
import { StringDevFieldNumber } from "./fields/StringDevFieldNumber";
import { StringDevFieldRange } from "./fields/StringDevFieldRange";
import { StringDevFieldColor } from "./fields/StringDevFieldColor";
import { StringDevFieldSelect } from "./fields/StringDevFieldSelect";
import { StringDevFieldToggle } from "./fields/StringDevFieldToggle";

interface BuiltFieldEntry {
  descriptor: Exclude<StringDevUIFieldDescriptor, { type: "divider" }>;
  field: StringDevUIField;
}

/**
 * Constructs a settings panel from a StringDevUIFieldDescriptor schema.
 *
 * The builder is module-agnostic — it reads the schema,
 * instantiates the correct field components, and wires up
 * change callbacks. The module never touches the UI directly.
 */
export class StringDevUIBuilder {
  private fields: StringDevUIField[] = [];
  private fieldEntries: BuiltFieldEntry[] = [];
  private values: Record<string, any> = {};

  /**
   * Builds a DocumentFragment containing grouped [data-stdg-panel-content] sections.
   * Each "divider" descriptor in the schema starts a new group.
   *
   * @param schema    Array of field descriptors from a devtool module
   * @param values    Current values (merged with defaults)
   * @param onChange  Callback fired when any field value changes
   * @returns         A DocumentFragment with one or more panel-content groups
   */
  build(
    schema: StringDevUIFieldDescriptor[],
    values: Record<string, any>,
    onChange: (key: string, value: any) => void,
  ): DocumentFragment {
    this.destroy();
    this.values = { ...values };
    const handleChange = (key: string, value: any) => {
      this.values[key] = value;
      this.syncDisabledStates();
      onChange(key, value);
    };

    const fragment = document.createDocumentFragment();
    let currentGroup = document.createElement("div");
    currentGroup.setAttribute("data-stdg-panel-content", "");
    fragment.appendChild(currentGroup);

    for (const descriptor of schema) {
      if (descriptor.type === "divider") {
        currentGroup = document.createElement("div");
        currentGroup.setAttribute("data-stdg-panel-content", "");
        fragment.appendChild(currentGroup);
        continue;
      }
      const field = this.createField(descriptor, values, handleChange);
      if (field) {
        this.fields.push(field);
        this.fieldEntries.push({ descriptor, field });
        currentGroup.appendChild(field.build());
      }
    }

    this.syncDisabledStates();

    return fragment;
  }

  /**
   * Updates all field values from an external state object.
   */
  updateValues(values: Record<string, any>): void {
    this.values = { ...this.values, ...values };
    for (const field of this.fields) {
      const key = (field as any).key;
      if (key && key in values) {
        field.setValue(values[key]);
      }
    }
    this.syncDisabledStates();
  }

  /**
   * Tears down all fields and removes the container.
   */
  destroy(): void {
    for (const field of this.fields) {
      field.destroy();
    }
    this.fields = [];
    this.fieldEntries = [];
    this.values = {};
  }

  private createField(
    descriptor: StringDevUIFieldDescriptor,
    values: Record<string, any>,
    onChange: (key: string, value: any) => void,
  ): StringDevUIField | null {
    switch (descriptor.type) {
      case "number":
        return new StringDevFieldNumber(
          descriptor.key,
          descriptor.label,
          values[descriptor.key] ?? descriptor.default,
          onChange,
          descriptor.min,
          descriptor.max,
          descriptor.step,
        );

      case "range":
        return new StringDevFieldRange(
          descriptor.key,
          descriptor.label,
          values[descriptor.key] ?? descriptor.default,
          onChange,
          descriptor.min,
          descriptor.max,
          descriptor.step,
          descriptor.displayMultiplier,
          descriptor.displayStep,
          descriptor.suffix,
          descriptor.units,
          values[descriptor.key + "Unit"] ?? descriptor.defaultUnit,
        );

      case "color":
        return new StringDevFieldColor(
          descriptor.key,
          descriptor.label,
          values[descriptor.key] ?? descriptor.default,
          onChange,
        );

      case "select":
        return new StringDevFieldSelect(
          descriptor.key,
          descriptor.label,
          values[descriptor.key] ?? descriptor.default,
          onChange,
          descriptor.options,
        );

      case "toggle":
        return new StringDevFieldToggle(
          descriptor.key,
          descriptor.label,
          values[descriptor.key] ?? descriptor.default,
          onChange,
        );

      default:
        return null;
    }
  }

  private syncDisabledStates(): void {
    for (const entry of this.fieldEntries) {
      const disabledWhen = entry.descriptor.disabledWhen;
      const disabled =
        !!disabledWhen && this.values[disabledWhen.key] === disabledWhen.equals;
      entry.field.setDisabled(disabled);
    }
  }
}
